// Modules
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"

// Constants
import PATHS from "~constants/paths"
import { ERROR_MESSAGES } from "~constants/errorMessages"

// Context
import { useAuthContext } from "~context/authContext"

// Services
import AuthService from "~services/AuthService"

// Components
import Input from "~app/Input"
import Title from "~styled/title"
import { FormSubmit } from "~styled/form"
import { AuthForm, AuthError, AuthDescription } from "~styled/auth"

interface ResetCodeFormProps {
  username: string
}

const ResetCodeForm = ({ username }: ResetCodeFormProps) => {
  const navigate = useNavigate()

  // Get from context
  const { handlePasswordReset } = useAuthContext()

  // Set component state
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>(null!)

  // Form definition
  const { formState: { errors }, register, clearErrors, handleSubmit } = useForm({
    defaultValues: { code: "" },
  })

  // Method to handle submit
  const onSubmit = async (data: { code: string }) => {
    setIsLoading(true)

    try {
      const response = await AuthService.getResetAuthCode(username, data.code)
      await handlePasswordReset(response.code)
      navigate(PATHS.passwordReset)
      return
    } catch (error) {
      setErrorMessage(ERROR_MESSAGES.GENERIC)
    }

    setIsLoading(false)
  }

  return (
    <AuthForm onSubmit={handleSubmit(onSubmit)}>
      <Title>Reset code</Title>

      <AuthDescription>Type reset code from your inbox.</AuthDescription>

      <Input
        name="code"
        label="Code"
        register={register("code", {
          required: true,
          minLength: {
            value: 4,
            message: "Code should have at least 4 characters.",
          },
          maxLength: {
            value: 6,
            message: "Code can't exceed 6 characters.",
          },
        })}
        onChange={() => clearErrors()}
        errorMessage={
          errors.code && (errors.code.message || ERROR_MESSAGES.FIELD_REQUIRED)
        }
      />

      <FormSubmit type="submit" disabled={isLoading}>
        Next
      </FormSubmit>

      {!!errorMessage && <AuthError>{errorMessage}</AuthError>}
    </AuthForm>
  )
}

export default ResetCodeForm
